import React, { useEffect, useState } from "react";
import InputField from "components/common/Input/Input";
import CustomButton from "components/common/Button/CustomButton";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "./styles.css";
import { toast } from "react-toastify";
import api from "store/services/sp.services";
import { LOADER_STATUS } from "store/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  stripeSaveCardDataRequest,
  clearStripeAttachSubscribeSuccess,
} from "store/actions/sp.actions";
import {
  getStripeSaveCardDataError,
  getStripeSaveCardDataLoader,
  getStripeAttachSubscribeSuccess,
} from "store/selector/sp.selector";
import { convertCentsToDollars } from "Utils/utils";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import PopoverContainer from "components/common/Popover/PopoverContainer";
import Loader from "components/common/Loader/Loader";
import {
  calculateCardProcessingFee,
  calculateTotalWithCardProcessingFee,
  CARD_PROCESSING_FEE_LABEL,
} from "pages/Stripe/stripeCardProcessingFee";
import {
  notifyStripeSubscriptionUpdated,
  syncAuthUserPaymentSetting,
} from "pages/Stripe/stripeSubscriptionRefresh";
import { authenticatedUser } from "store/actions/auth.actions";

const CARD_ELEMENT_OPTIONS = {
  showIcon: true,
  style: {
    base: {
      // fontSize: "16px",
      // color: "#424770",
      // "::placeholder": {
      //   color: "#aab7c4",
      // },
    },
  },
};

const SubscriptionCheckout = (props) => {
  const { handleBack, handleCloseCheckoutPage, data, defaultCardDetails } =
    props;
  const isNewSubscription = !data?.hasCurrentSubscription;
  
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const stripeFirstCallLoader = useSelector(getStripeSaveCardDataLoader);
  const stripeFirstCallError = useSelector(getStripeSaveCardDataError);
  const stripeAttachSubscribeSuccess = useSelector(
    getStripeAttachSubscribeSuccess
  );
  const [saveCardLoader, setSaveCardLoader] = useState(LOADER_STATUS.NA);

  useEffect(() => {
    if (stripeFirstCallLoader === false && saveCardLoader === 1) {
      setSaveCardLoader(LOADER_STATUS.NA);
      const hasError =
        stripeFirstCallError != null &&
        String(stripeFirstCallError).trim() !== "";
      const shouldNavigate =
        stripeAttachSubscribeSuccess || !hasError;
      if (hasError && !stripeAttachSubscribeSuccess) {
        setBuyPlanLoader(false);
        return;
      }
      if (shouldNavigate) {
        dispatch(clearStripeAttachSubscribeSuccess());
        handleCloseCheckoutPage();
      }
    }
  }, [stripeFirstCallLoader,
    saveCardLoader,
    stripeFirstCallError,
    stripeAttachSubscribeSuccess,
    dispatch,
    handleCloseCheckoutPage,]);

  // Show backend/Stripe error to user when subscribe via saved card fails (e.g. invalid coupon)
  useEffect(() => {
    if (stripeFirstCallLoader === false && stripeFirstCallError?.length > 0) {
      setSaveCardLoader(LOADER_STATUS.NA);
      setBuyPlanError(stripeFirstCallError);
      toast.error(stripeFirstCallError, { toastId: "stripeSubscribeError" });
    }
  }, [stripeFirstCallLoader, stripeFirstCallError]);

  const [buyPlanLoader, setBuyPlanLoader] = useState(false);
  const [buyPlanError, setBuyPlanError] = useState("");

  const [promoCode, setPromoCode] = useState("");
  const [isSubmitCoupon, setSubmitCoupon] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [onboardingFee, setOnboardingFee] = useState(null);
  const [onboardingFeeLoading, setOnboardingFeeLoading] = useState(false);
  const [onboardingCouponInfo, setOnboardingCouponInfo] = useState(null);
  const [onboardingDiscount, setOnboardingDiscount] = useState(0);

  const [disable, setDisabled] = useState(false);
  const [message, setMessage] = useState("");

  const [cardData, setCardData] = useState({
    name: "",
    // firstName: "",
    // lastName: "",
    // phone: "",
    // zip: "",
  });

  useEffect(() => {
    if (defaultCardDetails !== null) {
      setCardData((prev) => ({
        ...prev,
        name: defaultCardDetails.card_name,
        // firstName: defaultCardDetails.card_name?.split(" ")?.[0],
        // lastName: defaultCardDetails.card_name?.split(" ")?.[1],
      }));
    }
  }, [defaultCardDetails]);

  useEffect(() => {
    const priceId = data?.selectedPrice?.price_id;
    if (!priceId || !isNewSubscription) {
      setOnboardingFee(null);
      setOnboardingFeeLoading(false);
      return;
    }
    let cancelled = false;
    setOnboardingFeeLoading(true);
    api
      .stripeOnboardingFee(priceId)
      .then((res) => {
        if (cancelled) return;
        const fee = res?.onboarding_fee ?? res?.data?.onboarding_fee ?? null;
        setOnboardingFee(fee);
        setOnboardingFeeLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setOnboardingFee(null);
        }
        setOnboardingFeeLoading(false);
      });
    return () => { cancelled = true; };
  }, [data?.selectedPrice?.price_id, isNewSubscription]);

  const handleSubmitPaymentMethod = async () => {
    try {
      if (!stripe || !elements || disable) {
        return;
      }
      if (!cardData.name) {
        setMessage("Enter Card Holder Name");
        throw { message: "Enter Card Holder Name" };
      }
      setMessage("");
      setDisabled(true);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement) || { token: "123" },
      });
      setDisabled(false);
      if (error) {
        setMessage(error.message || "Incomplete card data");
        console.log("[error]", error.message);
        throw error;
      } else if (paymentMethod) {
        setMessage("");
        // console.log("[PaymentMethod]", paymentMethod);
        return paymentMethod;
      }
    } catch (err) {
      throw new Error(err?.message);
    }
  };

  const handleBuyPlan = async (event) => {
    event.preventDefault();
    let paymentMethod = null;
    try {
      if (
        data.selectedPrice === null ||
        !data.selectedPrice ||
        !data.selectedPrice?.price_id
      ) {
        return;
      }
      setBuyPlanLoader(true);
      if (defaultCardDetails === null) {
        paymentMethod = await handleSubmitPaymentMethod();
      }
      if (paymentMethod === null && defaultCardDetails === null) {
        setBuyPlanLoader(false);
        toast.error("Please Add Payment Method", {
          toastId: "add-payment-method-checkout-screen",
        });
        return;
      }

      const payload = {
        price_id: data.selectedPrice?.price_id,
      };
      if (couponInfo && couponInfo?.id) {
        Object.assign(payload, {
          coupon_id: couponInfo.id,
        });
      }
      if (isNewSubscription && onboardingCouponInfo?.id) {
        Object.assign(payload, {
          onboarding_coupon_id: onboardingCouponInfo.id,
        });
      }

      if (defaultCardDetails === null) {
        setBuyPlanError("");
        Object.assign(payload, {
          payment_method_id: paymentMethod.id,
          // card_holder_name: `${cardData.firstName} ${cardData.lastName}`,
          card_holder_name: cardData.name,
          is_default: true,
        });
        setSaveCardLoader(LOADER_STATUS.PENDING);
        dispatch(
          stripeSaveCardDataRequest(payload, {
            id: paymentMethod.id,
            card: paymentMethod?.card || null,
            metadata: {
              // card_name: `${cardData.firstName} ${cardData.lastName}`,
              card_name: cardData.name,
            },
          })
        );
      } else {
        setBuyPlanError("");
        const response = await api.stripeBuyPlanApi(payload);
        if (response) {
          setBuyPlanLoader(false);
          syncAuthUserPaymentSetting(response?.data, dispatch, authenticatedUser);
          notifyStripeSubscriptionUpdated();
          toast.info(
            "Thank you for subscribing! Your subscription is currently being processed and will be updated shortly."
          );
          handleCloseCheckoutPage();
        }
        setBuyPlanLoader(false);
      }
    } catch (err) {
      console.log("err", err);
      setBuyPlanLoader(false);
      if (defaultCardDetails !== null) {
        setBuyPlanError(err?.message ? err?.message : "Something Went Wrong!");
        toast.error(err?.message ? err?.message : "Something Went Wrong!");
      } else {
        toast.error(err?.message || "Something went wrong!", {
          toastId: "buyPlanAPI",
        });
      }
    }
  };

  const handleSubmitPromoCode = async () => {
    if (!promoCode?.trim()) {
      const msg = "Please enter a promo code.";
      setCouponError(msg);
      toast.error(msg, { toastId: "promoRequired" });
      return;
    }
    try {
      setSubmitCoupon(true);
      setCouponError("");
      const response = await api.stripeCouponInfo({
        coupon_name: promoCode.trim(),
        price_id: data?.selectedPrice?.price_id || undefined,
      });
      const payload = response?.coupon_info;
      const coupon = payload?.coupon_info ?? payload;
      const appliesTo = payload?.applies_to;
      if (!coupon || !coupon.valid) {
        setSubmitCoupon(false);
        return;
      }
      if (appliesTo === "none") {
        const msg = "This coupon is not valid for your selected plan or onboarding.";
        setCouponError(msg);
        toast.error(msg);
        setSubmitCoupon(false);
        return;
      }
      const planPrice = Number(
        data?.selectedPrice?.monthly?.split(" ")?.[0]?.replace("$", "") || 0
      );
      const onboardingAmountVal = Number(onboardingFee?.amount ?? 0);
      const discountPlan = getDiscount(planPrice, coupon);
      const discountOnb = getOnboardingDiscountDollars(onboardingAmountVal, coupon);
      let isApplied = false;
      if (appliesTo === "unknown") {
        const msg = "Invalid coupon for your plan.";
        setCouponError(msg);
        toast.error(msg);
        setSubmitCoupon(false);
        return;
      }
      if (!isNewSubscription && appliesTo === "onboarding") {
        const msg = "This coupon is only valid for onboarding on new subscriptions.";
        setCouponError(msg);
        toast.error(msg);
        setSubmitCoupon(false);
        return;
      }
      // Replace only the slot(s) this code applies to so plan + onboarding codes can both stay applied.
      if (appliesTo === "plan") {
        setCouponInfo(null);
        setDiscount(0);
      } else if (appliesTo === "onboarding") {
        setOnboardingCouponInfo(null);
        setOnboardingDiscount(0);
      } else if (appliesTo === "both") {
        setCouponInfo(null);
        setDiscount(0);
        setOnboardingCouponInfo(null);
        setOnboardingDiscount(0);
      }
      if (appliesTo === "plan" || appliesTo === "both") {
        if (planPrice - discountPlan < 0) {
          const msg = "This discount cannot be applied to your selected plan.";
          setCouponError(msg);
          toast.error(msg);
          setSubmitCoupon(false);
          return;
        }
        setCouponInfo(coupon);
        setDiscount(discountPlan);
        if (discountPlan > 0) isApplied = true;
      }
      if (appliesTo === "onboarding" || appliesTo === "both") {
        // Onboarding discount is only relevant on first purchase
        if (isNewSubscription && discountOnb > 0) {
          setOnboardingCouponInfo(coupon);
          setOnboardingDiscount(discountOnb);
          isApplied = true;
        }
      }
      if (!isApplied) {
        const msg = "This promo code does not apply to your current charges.";
        setCouponError(msg);
        toast.error(msg);
        setSubmitCoupon(false);
        return;
      }
      toast.success("Discount applied successfully.");
      setPromoCode("");
      setSubmitCoupon(false);
    } catch (err) {
      const msg = err?.message || "This promo code could not be applied. Please check the code and try again.";
      setCouponError(msg);
      setSubmitCoupon(false);
      toast.error(msg);
    }
  };

  const getDiscount = (price, coupon) => {
    if (coupon && coupon.percent_off !== null) {
      return price * (coupon.percent_off / 100);
    } else if (coupon && coupon.amount_off !== null && coupon.amount_off > 0) {
      return convertCentsToDollars(coupon.amount_off);
    } else {
      return 0;
    }
  };

  const getOnboardingDiscountDollars = (onboardingAmountDollars, coupon) => {
    const raw = getDiscount(onboardingAmountDollars, coupon);
    return Math.min(raw, Number(onboardingAmountDollars) || 0);
  };

  const planSubtotal = () =>
    Number(
      discount === 0 && couponInfo === null
        ? data?.selectedPrice?.monthly?.split(" ")?.[0]?.replace("$", "") || 0
        : (data?.selectedPrice?.monthly?.split(" ")?.[0]?.replace("$", "") || 0) - discount
    );
  const onboardingAmount = () =>
    isNewSubscription ? Number(onboardingFee?.amount ?? 0) : 0;
  const onboardingAfterDiscount = () =>
    Number(onboardingAmount() - Number(onboardingDiscount || 0));

  // Display: "Promo Price Apply" = sum of line items (Plan + Onboarding). GST is 10% of Promo Price Apply.
  const GST_RATE = 0.1;
  const subtotalBeforeGST = () => planSubtotal() + onboardingAfterDiscount();
  const calculateGST = () => subtotalBeforeGST() * GST_RATE;
  const amountBeforeCardFee = () => subtotalBeforeGST() + calculateGST();
  const calculateCardFee = () => calculateCardProcessingFee(amountBeforeCardFee());
  const calculateTotalAmount = () =>
    calculateTotalWithCardProcessingFee(amountBeforeCardFee());

  return (
    <div className='checkout-page w-full'>
      <div className='w-full flex gap-6'>
        <div className='w-[70%] checkout-payment-panel'>
          <div className='px-8 py-6'>
            <div className='mb-5'>
              <h1 className='checkout-section-title'>Add Payment Method</h1>
              <p className='checkout-section-subtitle'>
                Please add your credit card details.
              </p>
            </div>
            <div className='checkout-payment-card'>
            {typeof window !== "undefined" && window.location?.protocol === "http:" && (
              <div className='checkout-https-notice' role='status'>
                Payment autofill is disabled on this page because it’s not using a secure (HTTPS) connection. This is normal for local development; in production, use HTTPS and the browser warning will go away.
              </div>
            )}
            <div className='grid grid-cols-1 gap-4 py-1'>
              <div>
                <label className='checkout-field-label'>
                  Card Holder Name
                </label>
                <InputField
                  isDisabled={defaultCardDetails !== null}
                  type='text'
                  value={cardData.name}
                  placeHolder='Card Holder Name'
                  handleChange={(e) => {
                    if (defaultCardDetails !== null) return;
                    setCardData((prev) => ({
                      ...prev,
                      name: e,
                    }));
                  }}
                />
              </div>
              {/* <div className='col-span-1'>
                  <div className='pb-2'>
                    <label>Last Name</label>
                  </div>
                  <InputField
                    isDisabled={defaultCardDetails !== null}
                    type='text'
                    value={cardData.lastName}
                    placeHolder='Last Name'
                    handleChange={(e) => {
                      if (defaultCardDetails !== null) return;
                      setCardData((prev) => ({
                        ...prev,
                        lastName: e,
                      }));
                    }}
                  />
                </div> */}
            </div>
            {!stripe || !elements ? (
              <div className='py-8 flex flex-col items-center justify-center gap-4'>
                <CircularLoader
                  classes='flex justify-center items-center'
                  width_size='w-16'
                />
                <p className='text-sm text-gray-500'>
                  Loading secure payment form…
                </p>
              </div>
            ) : (
              <>
                <div className='grid grid-cols-1 gap-4 py-4'>
                  <div>
                    <label className='checkout-field-label'>
                      Card Number
                    </label>
                    <div className='stripe-input'>
                      {defaultCardDetails !== null ? (
                        <div className='stripe-input-disabled'>
                          {`**** **** **** ${defaultCardDetails.last4}`}
                        </div>
                      ) : (
                        <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                      )}
                    </div>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4 py-4'>
                  <div>
                    <label className='checkout-field-label'>
                      Expiry Date
                    </label>
                    <div className='stripe-input'>
                      {defaultCardDetails !== null ? (
                        <div className='stripe-input-disabled'>
                          {`${defaultCardDetails.exp_month} / ${defaultCardDetails.exp_year}`}
                        </div>
                      ) : (
                        <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className='checkout-field-label'>
                      CVC
                    </label>
                    <div className='stripe-input'>
                      {defaultCardDetails !== null ? (
                        <div className='stripe-input-disabled'>***</div>
                      ) : (
                        <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* <div className='grid grid-cols-2 gap-7 py-5'>
                <div className='col-span-1'>
                  <div className='pb-2'>
                    <label>Mobile Number</label>
                  </div>
                  <InputField
                    minLength='10'
                    pattern={/^0[0-9]*$/}
                    placeHolder='Enter Mobile Number'
                  />
                </div>
                <div className='col-span-1'>
                  <div className='pb-2'>
                    <label>ZIP</label>
                  </div>
                  <InputField placeHolder='A1B 2C3' />
                </div>
              </div> */}

            {/* Buttons */}
            {/* <div className='pt-5 flex justify-start gap-x-3'> ... */}
            </div>
          </div>
        </div>
        <div className='w-[30%] py-6 pr-6'>
          <div className='checkout-summary-card'>
            <h4 className='checkout-summary-title'>
              Order Summary
            </h4>
            <div className='checkout-summary-row'>
              <div className='checkout-summary-label'>Selected Plan</div>
              <div className='checkout-summary-value'>
                <span className='font-medium'>{data?.selectedPlan}</span>
                <span> — {data?.selectedPrice?.monthly}</span>
              </div>
            </div>
            <div className='checkout-summary-row'>
              <div className='checkout-summary-label'>Billing Schedule</div>
              <div className='checkout-summary-value capitalize'>
                {data?.selectedPrice?.plan_type}
              </div>
            </div>
            <div className='checkout-summary-row'>
              <div className='checkout-summary-label'>Number of Participants</div>
              <div className='checkout-participant-pill'>
                {data?.selectedParticipants}
              </div>
            </div>
            <div className='checkout-price-row'>
              <span className='checkout-summary-label'>Plan Price</span>
              <span>
                $
                {data?.selectedPrice?.monthly
                  ?.split(" ")?.[0]
                  ?.replace("$", "")}
              </span>
            </div>
            {isNewSubscription && (onboardingFeeLoading ? (
              <div className='checkout-price-row'>
                <span className='checkout-summary-label flex items-center gap-2'>
                  <Loader cssClass='w-5 h-5 text-[#4362a5]' />
                  Onboarding (one-time)
                </span>
                <span>Loading…</span>
              </div>
            ) : onboardingFee ? (
              <div className='checkout-price-row'>
                <span className='checkout-summary-label flex items-center gap-1'>
                  {onboardingFee.label || "Onboarding (one-time)"}
                  <PopoverContainer
                    toolTip={
                      <>
                        <p className='font-semibold mb-1'>Onboarding includes:</p>
                        <ul className='list-disc list-inside space-y-0.5 text-left'>
                          {["tier_1_5", "tier_6_10"].includes(onboardingFee?.tier) ? (
                            <>
                              <li>2 Onboarding training sessions</li>
                              <li>Permanent Technical Support</li>
                            </>
                          ) : (
                            <>
                              <li>5-6 Onboarding training sessions</li>
                              <li>Permanent Technical Support</li>
                            </>
                          )}
                        </ul>
                      </>
                    }
                  >
                    <span
                      className='inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#4362a5] text-white text-xs cursor-help font-bold'
                      aria-label='What does onboarding include?'
                    >
                      ?
                    </span>
                  </PopoverContainer>
                </span>
                <span>${Number(onboardingFee.amount ?? 0).toFixed(2)}</span>
              </div>
            ) : null)}
            <hr />
            <div className='checkout-price-row items-start gap-x-2'>
              <div className='flex-1 min-w-0'>
                <span className='checkout-summary-label block mb-2'>Promo Code</span>
                <InputField
                  cssClass={couponError ? "errorBorder" : ""}
                  value={promoCode}
                  handleChange={setPromoCode}
                  placeHolder='Enter your promo code'
                />
              </div>
              <div className='shrink-0 pt-6'>
                <CustomButton
                  isDisabled={isSubmitCoupon}
                  clickHandler={() => {
                    if (!promoCode || (promoCode && promoCode.length === 0)) {
                      toast.error("Promo Code is required!", {
                        toastId: "promoCodeError",
                      });
                      return;
                    }
                    handleSubmitPromoCode();
                  }}
                  label='Apply'
                  cssClass='mt-[2px]'
                />
              </div>
            </div>
            {couponError.length > 0 && (
              <div className='w-full mt-1 text-[13px] text-red-600'>
                {couponError}
              </div>
            )}
            {(discount > 0 || onboardingDiscount > 0) && (
              <div className='mt-2 text-sm py-2 px-3 promo-applied w-full'>
                {discount > 0 && onboardingDiscount > 0
                  ? "Discount applied to your plan and onboarding fee."
                  : discount > 0
                    ? "Discount applied to your plan."
                    : "Discount applied to your onboarding fee."}
              </div>
            )}
            {couponInfo && discount > 0 && (
              <div className='checkout-price-row'>
                <span className='checkout-summary-label'>Promo Discount (Plan)</span>
                <span>{`-$${discount}`}</span>
              </div>
            )}
            {couponInfo && discount > 0 && (
              <div className='checkout-price-row'>
                <span className='checkout-summary-label'>Price after discount</span>
                <span>
                  $
                  {Number(
                    data?.selectedPrice?.monthly
                      ?.split(" ")?.[0]
                      ?.replace("$", "")
                  ) - discount}
                </span>
              </div>
            )}
            {onboardingCouponInfo && onboardingDiscount > 0 && (
              <div className='checkout-price-row'>
                <span className='checkout-summary-label'>Onboarding Promo</span>
                <span>{`-$${Number(onboardingDiscount).toFixed(2)}`}</span>
              </div>
            )}
            <hr />
            <div className='py-2'>
              <div className='checkout-price-row'>
                <span className='checkout-summary-label'>Promo Price Apply</span>
                <span>${subtotalBeforeGST().toFixed(2)}</span>
              </div>
              <div className='checkout-price-row'>
                <span className='checkout-summary-label'>GST</span>
                <span>${calculateGST().toFixed(2)}</span>
              </div>
              {calculateCardFee() > 0 && (
                <div className='checkout-price-row'>
                  <span className='checkout-summary-label'>{CARD_PROCESSING_FEE_LABEL}</span>
                  <span>${calculateCardFee().toFixed(2)}</span>
                </div>
              )}
              <div className='checkout-price-row checkout-total-row'>
                <span className='checkout-summary-label'>Total</span>
                <span>${calculateTotalAmount().toFixed(2)}</span>
              </div>
              {calculateCardFee() > 0 && (
                <p className='mt-2 text-xs text-gray-500'>
                  A card processing fee applies to card payments to cover our payment provider costs.
                </p>
              )}
            </div>
            {(buyPlanError || stripeFirstCallError) && (
              <div className='mb-3 p-3 rounded-md bg-red-50 border border-red-200'>
                <p className='text-sm text-red-700'>
                  {buyPlanError || stripeFirstCallError}
                </p>
              </div>
            )}
            <div className='flex justify-start items-center space-x-2 w-full'>
              <div className='w-[70%]'>
                <CustomButton
                  showLoading={
                    stripeFirstCallLoader || buyPlanLoader || disable
                  }
                  isDisabled={disable || stripeFirstCallLoader || buyPlanLoader}
                  clickHandler={handleBuyPlan}
                  label={
                    stripeFirstCallLoader || buyPlanLoader || disable
                      ? "Processing…"
                      : "Subscribe Plan"
                  }
                  cssClass='rounded-lg w-full flex justify-center items-center gap-2'
                />
              </div>
              <div className='flex-shrink-0'>
                <CustomButton
                  clickHandler={() => handleBack()}
                  variant='secondary'
                  label='Cancel'
                  cssClass='rounded-lg px-4 flex justify-center items-center'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* </form> */}
    </div>
  );
};

export default SubscriptionCheckout;
